import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

/**
 * Sections 4 (How), 5 (ForCoaches), 6 (ForAthletes), 8 (FAQ), 9 (FinalCta)
 * from the design handoff. Class names match `reference/landing.css` verbatim;
 * everything is scoped under `.et-landing` in Home.css.
 *
 * Per user override: no "Ver demo" CTA, 30 días gratis instead of 14, and the
 * FinalCta dropdown CTAs go to signup.
 */

export const HowItWorks = () => (
  <section className="sec sec-how dark grid" data-screen-label="04 How it works">
    <div className="container">
      <div className="sec-head">
        <div className="eyebrow on-dark">
          <span className="eyebrow-dot" />
          <FormattedMessage id="landing.how.eyebrow" defaultMessage="CÓMO FUNCIONA" />
        </div>
        <h2 className="h2">
          <FormattedMessage id="landing.how.title" defaultMessage="Tres pasos. Cero planillas." />
        </h2>
        <p className="lede">
          <FormattedMessage
            id="landing.how.lede"
            defaultMessage="El flujo que tu cabeza ya hace, pero ahora en una sola herramienta — y con todos tus atletas viendo lo mismo que vos."
          />
        </p>
      </div>

      <div className="steps">
        <div className="step">
          <div className="step-num">01 · ARMÁS</div>
          <h3>Diseñás el plan.</h3>
          <p>
            Arrastrá ejercicios, definí series, repeticiones, carga y tempo. Guardalo como plantilla — vas a usarlo una
            y otra vez.
          </p>
          <div className="step-mock editor">
            <div className="row">
              <div className="bar" />
              <div className="name">Sentadilla</div>
              <div className="meta">4×6 · RPE 8</div>
            </div>
            <div className="row">
              <div className="bar b2" />
              <div className="name">Press banca</div>
              <div className="meta">5×5 · RPE 7.5</div>
            </div>
            <div className="row">
              <div className="bar" />
              <div className="name">Remo con barra</div>
              <div className="meta">4×8 · RPE 7</div>
            </div>
          </div>
        </div>

        <div className="step-arrow">
          <svg width="20" height="14" viewBox="0 0 20 14">
            <path
              d="M1 7H18M14 2L19 7L14 12"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="step">
          <div className="step-num">02 · ASIGNÁS</div>
          <h3>Lo bajás al calendario.</h3>
          <p>
            Una semana, un mesociclo, un atleta o cincuenta. Replicás con un click. Ajustás cargas individuales si
            querés.
          </p>
          <div className="step-mock cal">
            <div className="week-row">
              <span className="lbl">S1</span>
              <div className="day assigned" />
              <div className="day" />
              <div className="day assigned" />
              <div className="day" />
              <div className="day assigned" />
              <div className="day" />
              <div className="day" />
            </div>
            <div className="week-row">
              <span className="lbl">S2</span>
              <div className="day assigned" />
              <div className="day" />
              <div className="day assigned" />
              <div className="day" />
              <div className="day assigned" />
              <div className="day assigned" />
              <div className="day" />
            </div>
            <div className="week-row">
              <span className="lbl">S3</span>
              <div className="day assigned" />
              <div className="day assigned" />
              <div className="day assigned" />
              <div className="day" />
              <div className="day assigned" />
              <div className="day" />
              <div className="day" />
            </div>
            <div className="week-row">
              <span className="lbl">S4</span>
              <div className="day assigned" />
              <div className="day" />
              <div className="day assigned" />
              <div className="day" />
              <div className="day" />
              <div className="day" />
              <div className="day" />
            </div>
          </div>
        </div>

        <div className="step-arrow">
          <svg width="20" height="14" viewBox="0 0 20 14">
            <path
              d="M1 7H18M14 2L19 7L14 12"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="step">
          <div className="step-num">03 · MEDÍS</div>
          <h3>Y mirás los datos.</h3>
          <p>
            Volumen total, carga subjetiva promedio, adherencia, PRs. Sabés exactamente qué cambiar antes de la próxima
            sesión.
          </p>
          <div className="step-mock chart">
            <div className="head">
              <span className="lbl">VOLUMEN · MES</span>
            </div>
            <svg viewBox="0 0 200 55" preserveAspectRatio="none">
              <path
                d="M 0 45 L 25 38 L 50 30 L 75 34 L 100 24 L 125 18 L 150 14 L 175 9 L 200 4"
                stroke="#a3e635"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="200" cy="4" r="3" fill="#a3e635" />
            </svg>
            <div className="head">
              <span className="num">
                14.2t<span className="delta">+ 18%</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const ForCoach = ({ onSignUpClick }: { onSignUpClick: () => void }) => (
  <section className="sec sec-coach light" id="coaches" data-screen-label="05 Para coaches">
    <div className="container">
      <div className="split">
        <div className="split-copy">
          <div className="eyebrow on-light">
            <span className="eyebrow-dot" />
            <FormattedMessage id="landing.coaches.eyebrow" defaultMessage="PARA COACHES" />
          </div>
          <h2>
            <FormattedMessage id="landing.coaches.title" defaultMessage="Más atletas. Sin perder lo personal." />
          </h2>
          <p className="lede">
            <FormattedMessage
              id="landing.coaches.lede"
              defaultMessage="El cuello de botella no es tu conocimiento — es la administración. TrainEase se ocupa de eso para que vos vuelvas a entrenar gente."
            />
          </p>
          <ul className="checklist">
            <li>
              <div>
                <strong>Plantillas reutilizables</strong>
                <span>Buildá un plan una vez, asignalo a varios atletas con ajustes individuales.</span>
              </div>
            </li>
            <li>
              <div>
                <strong>Calendario unificado</strong>
                <span>Todos tus atletas en una sola vista. Quién entrena, quién falta, quién PRó.</span>
              </div>
            </li>
            <li>
              <div>
                <strong>Progreso en vivo</strong>
                <span>Ves al atleta marcar sets en tiempo real desde tu panel — sin abrir WhatsApp.</span>
              </div>
            </li>
            <li>
              <div>
                <strong>Comunicación integrada</strong>
                <span>Notas por sesión, ajustes por ejercicio, chat directo. Cero context-switching.</span>
              </div>
            </li>
          </ul>
          <button className="btn-ink-lg" type="button" onClick={onSignUpClick}>
            <FormattedMessage id="landing.coaches.cta" defaultMessage="Probar como coach" />
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="split-visual">
          <div className="window-big">
            <div className="chrome">
              <div className="lights">
                <span />
                <span />
                <span />
              </div>
              <div className="url">
                <span className="url-pill">trainease.com.ar/coach/calendario</span>
              </div>
              <div style={{ width: 60 }} />
            </div>
            <div className="coach-big-body">
              <div className="coach-big-head">
                <div>
                  <div className="title">Semana del 18 al 24 de mayo</div>
                  <div className="subtitle">7 ATLETAS · 23 SESIONES PROGRAMADAS</div>
                </div>
                <div className="live-pill">
                  <span className="dot" />
                  EN VIVO · 3
                </div>
              </div>

              <div className="coach-big-grid">
                <div>
                  <div className="coach-big-col-label">CALENDARIO</div>
                  <div className="coach-big-week">
                    <div className="day">
                      <div className="day-label">LUN</div>
                      <div className="day-num">18</div>
                      <div className="chip lime">Empuje</div>
                      <div className="chip">Tirón</div>
                    </div>
                    <div className="day">
                      <div className="day-label">MAR</div>
                      <div className="day-num">19</div>
                      <div className="chip">Pierna</div>
                    </div>
                    <div className="day today">
                      <div className="day-label">MIÉ</div>
                      <div className="day-num">20</div>
                      <div className="chip lime">Empuje</div>
                      <div className="chip lime">Tirón</div>
                    </div>
                    <div className="day">
                      <div className="day-label">JUE</div>
                      <div className="day-num">21</div>
                      <div className="chip">Pierna</div>
                    </div>
                    <div className="day">
                      <div className="day-label">VIE</div>
                      <div className="day-num">22</div>
                      <div className="chip">Full</div>
                      <div className="chip">Cardio</div>
                    </div>
                    <div className="day">
                      <div className="day-label">SÁB</div>
                      <div className="day-num">23</div>
                      <div className="chip">Cardio</div>
                    </div>
                    <div className="day">
                      <div className="day-label">DOM</div>
                      <div className="day-num">24</div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="coach-big-col-label">ATLETAS ACTIVOS HOY</div>
                  <div className="coach-big-athletes">
                    <div className="a live">
                      <div className="av" style={{ background: 'linear-gradient(135deg,#a3e635,#65a30d)' }}>
                        MS
                      </div>
                      <div className="info">
                        <div className="nm">Martina S.</div>
                        <div className="st">
                          <span className="d" />
                          Entrenando · set 3/4
                        </div>
                      </div>
                    </div>
                    <div className="a live">
                      <div className="av" style={{ background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)' }}>
                        JR
                      </div>
                      <div className="info">
                        <div className="nm">Joaquín R.</div>
                        <div className="st">
                          <span className="d" />
                          Entrenando · ej. 2/5
                        </div>
                      </div>
                    </div>
                    <div className="a">
                      <div className="av" style={{ background: 'linear-gradient(135deg,#f97316,#c2410c)' }}>
                        RK
                      </div>
                      <div className="info">
                        <div className="nm">Rocío K.</div>
                        <div className="st">
                          <span className="d" />
                          Programado · 16:00
                        </div>
                      </div>
                    </div>
                    <div className="a">
                      <div className="av" style={{ background: 'linear-gradient(135deg,#22c55e,#15803d)' }}>
                        DG
                      </div>
                      <div className="info">
                        <div className="nm">Diego G.</div>
                        <div className="st">
                          <span className="d" />
                          Completó · 11:30
                        </div>
                      </div>
                    </div>
                    <div className="a">
                      <div className="av" style={{ background: 'linear-gradient(135deg,#ef4444,#b91c1c)' }}>
                        VL
                      </div>
                      <div className="info">
                        <div className="nm">Valentina L.</div>
                        <div className="st">
                          <span className="d" />
                          No marcó hoy
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const ForAthlete = ({ onSignUpClick }: { onSignUpClick: () => void }) => (
  <section className="sec sec-athlete dark grid" id="atletas" data-screen-label="06 Para atletas">
    <div className="container">
      <div className="split reverse">
        <div className="split-visual">
          <div className="window-big">
            <div className="chrome">
              <div className="lights">
                <span />
                <span />
                <span />
              </div>
              <div className="url">
                <span className="url-pill">trainease.com.ar/sesion</span>
              </div>
              <div style={{ width: 60 }} />
            </div>
            <div className="ath-big-body">
              <div className="ath-big-head">
                <div>
                  <div className="ath-big-name">EJERCICIO 2 / 5</div>
                  <div className="ath-big-title">Press banca</div>
                </div>
                <div>
                  <div className="ath-big-prog">PROGRESO HOY</div>
                  <div className="ath-big-prog" style={{ textAlign: 'right', marginTop: 2 }}>
                    <strong>32%</strong>
                  </div>
                </div>
              </div>

              <div className="ath-big-tabs">
                <button className="active" type="button">
                  Sesión
                </button>
                <button type="button">Historial</button>
                <button type="button">Progreso</button>
              </div>

              <div className="ath-big-display">
                <div className="blk primary">
                  <div className="lbl">SET ACTUAL</div>
                  <div className="num">
                    3<span className="of"> / 5</span>
                  </div>
                </div>
                <div className="blk">
                  <div className="lbl">OBJETIVO</div>
                  <div className="num">6 reps</div>
                  <div className="sub">@ 80 kg · RPE 7.5</div>
                </div>
                <div className="blk">
                  <div className="lbl">DESCANSO</div>
                  <div className="num">2:00</div>
                  <div className="sub">próximo set</div>
                </div>
              </div>

              <div className="ath-big-sets">
                {[1, 2, 3, 4, 5].map((n) => {
                  const cls = n <= 2 ? 'set done' : n === 3 ? 'set active' : 'set';
                  const label = n === 3 ? 'Set 3 · en curso' : `Set ${n}`;
                  const vals = n <= 2 ? '6 × 80kg' : '— × 80kg';
                  return (
                    <div className={cls} key={n}>
                      <div className="check">
                        <svg width="11" height="11" viewBox="0 0 11 11">
                          <path
                            d="M2 5.5L5 8.5L9 3"
                            stroke="#0a0a0a"
                            strokeWidth="2"
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="lbl">{label}</div>
                      <div className="vals">{vals}</div>
                    </div>
                  );
                })}
              </div>

              <button className="ath-big-cta" type="button" onClick={onSignUpClick}>
                Marcar set ✓
              </button>
            </div>
          </div>
        </div>

        <div className="split-copy">
          <div className="eyebrow on-dark">
            <span className="eyebrow-dot" />
            <FormattedMessage id="landing.athletes.eyebrow" defaultMessage="PARA ATLETAS" />
          </div>
          <h2>
            <FormattedMessage id="landing.athletes.title" defaultMessage="Tu plan. Tu progreso. En el navegador." />
          </h2>
          <p className="lede">
            <FormattedMessage
              id="landing.athletes.lede"
              defaultMessage="Abrís un link, marcás sets, y cerrás. Sin descargar nada, sin pedirle ayuda a tu coach por WhatsApp cada vez."
            />
          </p>
          <ul className="checklist">
            <li>
              <div>
                <strong>Tu sesión te espera</strong>
                <span>Entrás, ves tu próximo entrenamiento. Sin buscar, sin pedir, sin abrir Excel.</span>
              </div>
            </li>
            <li>
              <div>
                <strong>Sin instalar nada</strong>
                <span>Funciona en cualquier celular o computadora. Solo necesitás un navegador.</span>
              </div>
            </li>
            <li>
              <div>
                <strong>Tu progreso visible</strong>
                <span>PRs, volumen total, evolución por ejercicio. Sabés cuánto avanzaste — no solo tu coach.</span>
              </div>
            </li>
            <li>
              <div>
                <strong>Tu coach a mano</strong>
                <span>Una pregunta, un ajuste, una duda — todo en la misma app. Sin chat secundario.</span>
              </div>
            </li>
          </ul>
          <button className="btn-lime-lg" type="button" onClick={onSignUpClick}>
            <FormattedMessage id="landing.athletes.cta" defaultMessage="Comenzar gratis" />
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>
);

const FAQ_ITEMS: Array<{ q: string; a: string }> = [
  {
    q: '¿Mis atletas tienen que instalar algo?',
    a: 'No. TrainEase corre 100% en el navegador. Tus atletas reciben un link, lo abren, marcan sets y se van. Funciona en celular, tablet y computadora.'
  },
  {
    q: '¿Puedo migrar mis planillas Excel?',
    a: 'Sí. Importamos CSV y XLSX con un mapeo rápido (ejercicio, sets, reps, peso). Si tu planilla es muy custom, te ayudamos en el onboarding del plan Pro o superior.'
  },
  {
    q: '¿Cuánto tarda armar un ciclo entero?',
    a: 'Un coach Pro arma un mesociclo de 8 semanas en menos de 10 minutos usando plantillas. Si lo hacés desde cero la primera vez, contate unos 20-30 minutos por plantilla.'
  },
  {
    q: '¿Hay app móvil nativa?',
    a: 'Próximamente. Por ahora, la web está optimizada mobile-first — tus atletas la usan desde el celular sin problema. Cuando salga la app iOS / Android, todas las cuentas existentes la van a tener incluida.'
  },
  {
    q: '¿Y si quiero cancelar?',
    a: 'Cancelás desde tu cuenta con un click. Sin formulario, sin call de retención. Tus datos quedan exportables por 90 días por si querés volver.'
  },
  {
    q: '¿Qué métodos de medición subjetiva soporta?',
    a: 'RPE, RIR, frecuencia cardíaca y métodos custom. Cada coach configura los que usa con sus atletas y el editor de planes los muestra como pills directas.'
  },
  {
    q: '¿Cobran comisión a mis atletas?',
    a: 'No. La relación comercial con tus atletas es tuya — TrainEase es solo la herramienta. Tu precio al atleta lo definís vos, nosotros no nos metemos.'
  },
  {
    q: '¿Funciona para CrossFit / functional / running?',
    a: 'Sí. El editor maneja tiempo, distancia, reps, carga subjetiva, tempo y peso. La biblioteca de ejercicios cubre fuerza, hipertrofia, CrossFit y resistencia. Si te falta algo, lo agregás vos.'
  }
];

export const FAQ = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <section className="sec sec-faq light" id="faq" data-screen-label="08 FAQ">
      <div className="container">
        <div className="sec-head">
          <div className="eyebrow on-light">
            <span className="eyebrow-dot" />
            <FormattedMessage id="landing.faq.eyebrow" defaultMessage="PREGUNTAS" />
          </div>
          <h2 className="h2">
            <FormattedMessage id="landing.faq.title" defaultMessage="Lo que nos preguntan más seguido." />
          </h2>
        </div>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className={`faq-item${openIdx === i ? ' open' : ''}`}>
              <button
                className="faq-q"
                type="button"
                aria-expanded={openIdx === i}
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
              >
                {item.q}
                <span className="icon" />
              </button>
              <div className="faq-a">
                <div className="faq-a-inner">{item.a}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FinalCta = ({ onSignUpClick }: { onSignUpClick: () => void }) => (
  <section className="sec-cta" data-screen-label="09 CTA final">
    <div className="sec-cta-inner">
      <h2>
        <FormattedMessage id="landing.finalCta.title" defaultMessage="Empezá " />
        <span className="accent">
          <FormattedMessage id="landing.finalCta.titleAccent" defaultMessage="hoy" />
        </span>
        .
      </h2>
      <p className="lede">
        <FormattedMessage
          id="landing.finalCta.lede"
          defaultMessage="30 días gratis. Sin tarjeta. Sin contrato. Si no te sirve, te vas y no pasa nada."
        />
      </p>
      <div className="ctas">
        <button className="btn-lime-lg" type="button" onClick={onSignUpClick}>
          <FormattedMessage id="landing.finalCta.cta" defaultMessage="Comenzar gratis" />
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8h10M9 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="sec-cta-trust">
        <span>Sin tarjeta</span>
        <span>Sin contrato</span>
        <span>Cancelás cuando quieras</span>
      </div>
    </div>
  </section>
);
